/**
 * Restricts the state of a Shopify form to the form that was actually submitted.
 *
 * Shopify raises `form.posted_successfully?` and `form.errors` on EVERY `{% form %}` of the same
 * type on the page, and `form.id` reads null inside the block even when an id is passed (measured
 * on this theme on 2026-09-23), so Liquid cannot tell them apart. Sections therefore render their
 * state unconditionally, which stays correct without JavaScript, and this module removes the state
 * of the forms that did not own the submission.
 *
 * The owner is recorded at submit time, in sessionStorage. The fragment Shopify appends to the form
 * action (`/contact#<id>`) cannot carry it: it is lost whenever the submission goes through the
 * captcha challenge, which is exactly when every form of the page ended up showing a message.
 *
 * An empty record (a submitted form without an id) means the owner is unknown: nothing is removed,
 * rather than hiding the only message the visitor could act on.
 */
const STORAGE_KEY = 'locks-passion-submitted-form';

/** @returns {string} id of the last Shopify form submitted in this tab, empty when unknown. */
export function submittedFormId() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.querySelector('input[name="form_type"]')) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, form.id);
    } catch {
      // Storage unavailable (private browsing, quota): the owner stays unknown, nothing is removed.
    }
  },
  { capture: true },
);

const owner = submittedFormId();
if (owner) {
  document.querySelectorAll('[data-customer-form-state]').forEach((node) => {
    if (!(node instanceof HTMLElement) || node.dataset.customerFormState === owner) return;
    document.querySelectorAll(`[aria-describedby="${node.id}"]`).forEach((field) => {
      field.removeAttribute('aria-describedby');
      field.removeAttribute('aria-invalid');
    });
    node.remove();
  });
}
